import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw } from "lucide-react";
import type { LifecycleResponse } from "../../shared/types";
import { api } from "../lib/api";
import { PageHeader } from "../components/app-shell";
import { LinkTable } from "../components/link-table";
import { Button, Empty, ErrorState, Loading, Tag, useToast } from "../components/ui";

export function LifecyclePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expiringPage, setExpiringPage] = useState(1);
  const [gracePage, setGracePage] = useState(1);
  const [releasedPage, setReleasedPage] = useState(1);
  const query = useQuery({
    queryKey: ["lifecycle", expiringPage, gracePage, releasedPage],
    queryFn: () => {
      const params = new URLSearchParams({
        pageSize: "20",
        expiringPage: String(expiringPage),
        gracePage: String(gracePage),
        releasedPage: String(releasedPage),
      });
      return api<LifecycleResponse>(`/api/lifecycle?${params.toString()}`);
    },
  });
  const runMutation = useMutation({
    mutationFn: () => api<{ movedToGrace: number; released: number; linksDeleted: number; mailboxesDeleted: number }>("/api/lifecycle/run", { method: "POST" }),
    async onSuccess(result) {
      toast.push(`处理完成：${result.movedToGrace} 条进入宽限，物理删除 ${result.linksDeleted} 条短链、${result.mailboxesDeleted} 个邮箱`);
      await queryClient.invalidateQueries();
    },
    onError: toast.fromError,
  });
  return (
    <div className="page lifecycle-page">
      <PageHeader eyebrow="LIFECYCLE · 到期与释放" title="短链的一生" body="到期先进入宽限期；释放冷却结束后物理删除记录，让后缀重新回到池中。" actions={<Button variant="primary" onClick={() => runMutation.mutate()} disabled={runMutation.isPending}><Play size={15} />立即执行清理</Button>} />
      {query.isLoading ? <Loading label="读取生命周期" /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : query.data ? (
        <>
          <section className="lifecycle-track" aria-label="生命周期阶段">
            <div><span>01</span><strong>生效</strong><p>正常跳转并记录每日点击。</p></div>
            <div><span>02</span><strong>到期</strong><p>在设定日期抵达边界。</p></div>
            <div className="lifecycle-track__accent"><span>03</span><strong>宽限 {query.data.policy.gracePeriodDays} 天</strong><p>仍可跳转，管理员或用户可以续期。</p></div>
            <div><span>04</span><strong>释放</strong><p>目标地址清除，跳转停止。</p></div>
            <div><span>05</span><strong>冷却 {query.data.policy.releasedCooldownDays} 天</strong><p>结束后物理删除，后缀重新可分配。</p></div>
          </section>
          <LifecycleGroup title="即将到期" eyebrow="NEXT 30 DAYS" section={query.data.expiring} page={expiringPage} setPage={setExpiringPage} />
          <LifecycleGroup title="宽限期" eyebrow="GRACE · ACTION NEEDED" section={query.data.grace} page={gracePage} setPage={setGracePage} accent />
          <LifecycleGroup title="已释放" eyebrow="RELEASED · COOLING" section={query.data.released} page={releasedPage} setPage={setReleasedPage} />
          <footer className="policy-note"><RefreshCw size={17} /><p><strong>自动执行。</strong> Cloudflare 使用每日 Cron Trigger；独立服务器每小时检查一次。手动运行与自动任务使用完全相同的幂等规则。</p></footer>
        </>
      ) : null}
    </div>
  );
}

function LifecycleGroup({ title, eyebrow, section, page, setPage, accent = false }: { title: string; eyebrow: string; section: LifecycleResponse["expiring"]; page: number; setPage: React.Dispatch<React.SetStateAction<number>>; accent?: boolean }) {
  return (
    <section className={`lifecycle-group ${accent ? "lifecycle-group--accent" : ""}`}>
      <header><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div><Tag tone={accent ? "accent" : "neutral"}>{section.pagination.total} LINKS</Tag></header>
      {section.items.length ? <LinkTable links={section.items} compact /> : <Empty title={`没有${title}短链`} body="当前没有需要处理的项目。" />}
      <footer className="pagination"><span>第 {section.pagination.page} / {section.pagination.totalPages} 页</span><div><Button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>← 上一页</Button><Button disabled={page >= section.pagination.totalPages} onClick={() => setPage((value) => value + 1)}>下一页 →</Button></div></footer>
    </section>
  );
}
